import { useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { CustomFlowbiteTheme, Dropdown } from 'flowbite-react';
import { IoCodeSlashSharp } from 'react-icons/io5';
import { GetAllMeetingsDetailAPIResponse, UserPercentileBucket } from 'types';
import { convertEnumToTitleCaseProfileTab } from 'shared-utils';

const MultiProfileTabs = ({
	allMeetingsDetailData,
	selectedProfileTab,
	setSelectedProfileTab,
	profileSpecificPercentiles,
	onlyPublicProfile,
}: {
	allMeetingsDetailData?: GetAllMeetingsDetailAPIResponse;
	selectedProfileTab: string;
	setSelectedProfileTab: (tab: string) => void;
	profileSpecificPercentiles?: {
		[key: string]: UserPercentileBucket;
	};
	onlyPublicProfile: boolean;
}) => {
	const getRankBadge = (userPercentileBucketData: UserPercentileBucket) => {
		if (userPercentileBucketData === UserPercentileBucket.TOP_ONE) {
			return 'Top 1%';
		}
		if (userPercentileBucketData === UserPercentileBucket.TOP_TEN) {
			return 'Top 10%';
		}
		if (userPercentileBucketData === UserPercentileBucket.TOP_TWENTY_FIVE) {
			return 'Top 25%';
		}
		if (userPercentileBucketData === UserPercentileBucket.TOP_FIFTY) {
			return 'Top 50%';
		}
	};
	const mergedUserPreference = allMeetingsDetailData?.mergedUserPreference || {};
	const [allTabsKeys, setAllTabsKey] = useState<string[]>(
		Object.keys(mergedUserPreference)?.filter(
			key =>
				allMeetingsDetailData?.meetings?.some(
					meeting => meeting.mergedUserPreference === key && meeting?.meetingRecordings?.length,
				),
		),
	);

	useEffect(() => {
		if (allTabsKeys.length > 0 && !selectedProfileTab) {
			setSelectedProfileTab(allTabsKeys[0]);
		}
	}, [allTabsKeys, setSelectedProfileTab, selectedProfileTab]);

	const isTablet = useMediaQuery('(max-width:1084px)');
	const isMobile = useMediaQuery('(max-width:918px)');
	const customTheme: CustomFlowbiteTheme['dropdown'] = {
		floating: {
			target:
				'border-box h-[72px] px-4 py-3 text-sm font-medium text-gray-900 bg-[#FFF] border-solid border-[2px] border-[#F2F2F5] rounded-t-2xl hover:bg-gray-100 border-b-0',
		},
	};
	const maxVisibleTabs = isMobile ? 1 : isTablet ? 1 : 4;
	const remainingTabs = allTabsKeys.slice(maxVisibleTabs);

	const getUserPreferenceData = (key: string) => {
		const data = key.split('-');
		const careerPath = data[1];
		const developmentRole = data[2];
		const techStack = data[3];
		return {
			careerPath: convertEnumToTitleCaseProfileTab(careerPath),
			developmentRole: convertEnumToTitleCaseProfileTab(developmentRole),
			techStack: convertEnumToTitleCaseProfileTab(techStack),
		};
	};

	const handleDropDownClick = (key: string) => {
		const selectedTab = allTabsKeys.find(tab => tab === key);
		const reorderedTabs = [selectedTab, ...allTabsKeys.filter(tab => tab !== key)] as string[];
		setAllTabsKey(reorderedTabs);
		setSelectedProfileTab(key);
	};

	return (
		<div className="flex items-center space-x-0 border-solid border-b-[1px] sm:border-b-0 border-[#C7C9D9]">
			{!isMobile ? (
				<>
					{allTabsKeys.slice(0, maxVisibleTabs).map(key => (
						<button
							key={key}
							className={`px-4 py-3 text-sm font-medium text-gray-900 bg-[#FFF] border-solid border-[2px] border-[#F2F2F5] rounded-t-2xl hover:bg-gray-100 border-b-0 
							${selectedProfileTab === key ? 'border-purple-400 !bg-[#F7F9FB]' : ''}`}
							onClick={() => {
								setSelectedProfileTab(key);
							}}
						>
							<div className={`flex gap-3`}>
								<div className="relative">
									<img src="/assets/Polygon.svg" alt="" />

									<div className="absolute top-[50%] left-[50%] transform -translate-x-1/2 -translate-y-1/2 h-4 w-4">
										<IoCodeSlashSharp />
									</div>
								</div>
								<div className="flex flex-col gap-2 justify-between">
									<p className="text-[#1F2A37] font-inter font-medium text-sm tracking-wider">
										{getUserPreferenceData(key)?.careerPath}
										{getUserPreferenceData(key)?.developmentRole?.toLowerCase() !== 'other'
											? ` - ${getUserPreferenceData(key)?.developmentRole}`
											: null}
									</p>

									<div className="flex justify-between items-center">
										{/* {tabItem.skill ? <span className="text-xs font-medium text-[#1F2A37]">{tabItem?.skill}</span> : null} */}
										{getUserPreferenceData(key)?.techStack?.toLowerCase() !== 'other' && (
											<span className="text-xs font-medium text-[#1F2A37]">
												{getUserPreferenceData(key)?.techStack}
											</span>
										)}
										{onlyPublicProfile && profileSpecificPercentiles?.[key] && (
											<span className="text-xs font-medium text-[#00B7C4] bg-[#E5FFFF] px-2 py-0.5 rounded-full ml-auto border border-[#00B7C4]">
												{getRankBadge(profileSpecificPercentiles?.[key])}
											</span>
										)}
									</div>
								</div>
							</div>
						</button>
					))}
					{remainingTabs.length > 0 && (
						<Dropdown
							color="white"
							theme={customTheme}
							label={
								<div className="flex items-center justify-center">
									<div className="flex items-center justify-center">
										<p className=" text-[#1F2A37] font-inter text-sm">{remainingTabs.length} more</p>
									</div>
								</div>
							}
							dismissOnClick={true}
						>
							{remainingTabs.map(key => (
								<Dropdown.Item
									key={key}
									onClick={() => {
										handleDropDownClick(key);
									}}
								>
									<div className="flex gap-2 items-center justify-between w-full">
										<p>
											{getUserPreferenceData(key)?.careerPath}
											{getUserPreferenceData(key)?.developmentRole?.toLowerCase() !== 'other'
												? ` - ${getUserPreferenceData(key)?.developmentRole}`
												: null}{' '}
										</p>
										{getUserPreferenceData(key)?.techStack?.toLowerCase() !== 'other' && (
											<span className="text-xs font-medium text-[#00B7C4] bg-[#E5FFFF] px-2 py-0.5 rounded-full ml-auto border border-[#00B7C4]">
												{getUserPreferenceData(key)?.techStack}
											</span>
										)}
									</div>
								</Dropdown.Item>
							))}
						</Dropdown>
					)}
				</>
			) : (
				<div className="flex w-full">
					<button
						className="px-4 py-3 text-sm font-medium text-gray-900 bg-[#FFF] border-solid border-[2px] rounded-t-2xl hover:bg-gray-100 border-b-0 
							 border-purple-400 w-full flex gap-2 justify-between"
					>
						<div className={`flex gap-3`}>
							<div className="relative">
								<img src="/assets/Polygon.svg" alt="" />

								<div className="absolute top-[50%] left-[50%] transform -translate-x-1/2 -translate-y-1/2 h-4 w-4">
									<IoCodeSlashSharp />
								</div>
							</div>
							<div className="flex flex-col gap-2 justify-between">
								<p className="text-[#1F2A37] font-inter font-medium text-sm tracking-wider">
									{getUserPreferenceData(selectedProfileTab)?.careerPath}
									{getUserPreferenceData(selectedProfileTab)?.developmentRole?.toLowerCase() !== 'other'
										? ` - ${getUserPreferenceData(selectedProfileTab)?.developmentRole}`
										: null}
								</p>

								<div className="flex justify-between items-center">
									{/* {tabItem.skill ? <span className="text-xs font-medium text-[#1F2A37]">{tabItem?.skill}</span> : null} */}
									{getUserPreferenceData(selectedProfileTab)?.techStack?.toLowerCase() !== 'other' && (
										<span className="text-xs font-medium text-[#1F2A37]">
											{getUserPreferenceData(selectedProfileTab)?.techStack}
										</span>
									)}
									{onlyPublicProfile && profileSpecificPercentiles?.[selectedProfileTab] && (
										<span className="text-xs font-medium text-[#00B7C4] bg-[#E5FFFF] px-2 py-0.5 rounded-full ml-auto border border-[#00B7C4]">
											{getRankBadge(profileSpecificPercentiles?.[selectedProfileTab])}
										</span>
									)}
								</div>
							</div>
						</div>

						<div className="flex h-full justify-center items-center">
							{remainingTabs.length > 0 && (
								<Dropdown color="white" label={null} dismissOnClick={true}>
									{remainingTabs.map(key => (
										<Dropdown.Item
											key={key}
											onClick={() => {
												handleDropDownClick(key);
											}}
										>
											<div className="flex gap-2 items-center justify-between w-full">
												<p>
													{getUserPreferenceData(key)?.careerPath}
													{getUserPreferenceData(key)?.developmentRole?.toLowerCase() !== 'other'
														? ` - ${getUserPreferenceData(key)?.developmentRole}`
														: null}{' '}
												</p>
												{getUserPreferenceData(key)?.techStack?.toLowerCase() !== 'other' && (
													<span className="text-xs font-medium text-[#00B7C4] bg-[#E5FFFF] px-2 py-0.5 rounded-full ml-auto border border-[#00B7C4]">
														{getUserPreferenceData(key)?.techStack}
													</span>
												)}
											</div>
										</Dropdown.Item>
									))}
								</Dropdown>
							)}
						</div>
					</button>
				</div>
			)}
		</div>
	);
};

export default MultiProfileTabs;
